// Pure helpers for the volunteer dashboard's participation stats and its merged
// History table. Kept free of React so the merge rules are unit-testable.
//
// A volunteer earns hours two ways: attending an event (approved by an admin
// after check-in) and self-directed work logged as an approved WorkLog. Both
// feed the same two stat cards and the same History table.

const MS_PER_HOUR = 1000 * 60 * 60;

const round1 = (value) => Math.round(value * 10) / 10;

// A plain YYYY-MM-DD. Splitting it avoids the day shifting in negative-offset
// timezones, which `new Date("2026-07-15")` would do.
export const formatRowDate = (value) => {
    if (!value) return "TBD";
    const [y, m, d] = value.split("-");
    return `${m}/${d}/${y}`;
};

// A local YYYY-MM-DD for an event datetime, so events and work logs land in the
// same month bucket under the viewer's timezone.
const localIsoDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

// Local midnight, so a work-log date sorts against event datetimes coherently.
const workDateSortKey = (value) => {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d).getTime();
};

// Hours run from the event's scheduled start, not the check-in: arriving early
// or late does not change the credit. The check-in still has to exist -- it is
// the evidence the volunteer turned up -- and an admin-recorded check-out still
// cuts the span short for an early departure.
export const eventHours = (reservation) => {
    if (!reservation.attended_at) return 0;
    const startTime = new Date(reservation.event?.start_date);
    const endTime = reservation.check_out
        ? new Date(reservation.check_out)
        : new Date(reservation.event?.end_date);
    return Math.max(0, (endTime - startTime) / MS_PER_HOUR);
};

// An admin can set attendance_status without attended_at (both are optional on
// AdminReservationUpdateSerializer). Counting such a row would turn
// `new Date(null)` into the epoch and balloon the total by ~490,000 hours, so
// the check-in timestamp is required too.
export const isCountableEvent = (reservation) =>
    reservation.attendance_status === "approved" && Boolean(reservation.attended_at);

const isApprovedLog = (log) => log.status === "approved";

export const computeParticipation = (reservations, workLogs) => {
    const countableEvents = reservations.filter(isCountableEvent);
    const approvedLogs = workLogs.filter(isApprovedLog);

    const eventTime = countableEvents.reduce((acc, res) => acc + eventHours(res), 0);
    const workTime = approvedLogs.reduce((acc, log) => acc + Number(log.hours), 0);

    return {
        time: round1(eventTime + workTime),
        number: countableEvents.length + approvedLogs.length,
    };
};

// Events have no rejected state -- an admin either approves, rules the row a
// no-show, or leaves it alone. `no_show` is checked first because it outranks
// the check-in timestamp: someone who pressed Check in without turning up keeps
// that timestamp, and the admin's ruling is what the row means. An approval with
// no check-in timestamp is possible via the admin serializer and cannot be
// trusted, so it reads as not-checked-in. A null status is a row nobody has
// ruled on yet -- the scheduler settles those 24 hours after the event ends.
export const eventStatus = (reservation) => {
    if (reservation.attendance_status === "no_show") return "no-show";
    if (isCountableEvent(reservation)) return "approved";
    if (reservation.attended_at) return "pending";
    return "not-checked-in";
};

// Every ended event plus every work log, normalized to one shape and sorted
// newest first. Reservation ids and work log ids are independent sequences and
// do collide, so keys are prefixed by source. `hours` stays unrounded here so
// month subtotals match the dashboard totals; rounding happens at display time.
export const buildServiceRows = (reservations, workLogs, now) => {
    const eventRows = reservations
        .filter((res) => {
            const endDate = res.event?.end_date ? new Date(res.event.end_date) : null;
            return endDate && endDate < now;
        })
        .map((res) => {
            const start = new Date(res.event.start_date);
            return {
                key: `event-${res.id}`,
                source: "event",
                name: res.event?.title || "Event",
                isoDate: localIsoDate(start),
                sortKey: start.getTime(),
                hours: isCountableEvent(res) ? eventHours(res) : null,
                status: eventStatus(res),
                eventPk: res.event?.pk ?? null,
                rejectionReason: "",
            };
        });

    const logRows = workLogs.map((log) => ({
        key: `work-${log.id}`,
        source: "independent",
        name: log.description,
        isoDate: log.work_date,
        sortKey: workDateSortKey(log.work_date),
        hours: Number(log.hours),
        status: log.status,
        eventPk: null,
        rejectionReason: log.rejection_reason || "",
    }));

    return [...eventRows, ...logRows].sort((a, b) => b.sortKey - a.sortKey);
};

// Rows for the dashboard History panel: every event that has already ended
// (regardless of whether attendance was approved) plus every approved work log.
// Display-rounded and stripped down to the panel's three columns.
export const buildActivityRows = (reservations, workLogs, now) =>
    buildServiceRows(reservations, workLogs, now)
        .filter((row) => row.source === "event" || row.status === "approved")
        .map((row) => ({
            key: row.key,
            name: row.name,
            date: formatRowDate(row.isoDate),
            hours: row.hours === null ? null : round1(row.hours),
        }));
